<template>
  <div class="page-shell tk-page">
    <!-- ============ 工具栏 ============ -->
    <div class="toolbar">
      <div class="tb-row">
        <span class="toolbar-label">信号日</span>
        <el-select v-model="date" class="sel-date" size="small" @change="loadSignals">
          <el-option v-for="d in dates" :key="d" :value="d" :label="dLabel(d)" />
        </el-select>
        <el-tag v-if="lagDays" size="mini" type="warning">
          ⚠️ 指标源滞后 {{ lagDays }} 个交易日（展示价已到 {{ barLatest }}）
        </el-tag>

        <span class="toolbar-label">股票池</span>
        <el-tooltip :content="poolTip" placement="bottom">
          <span class="caliber-chip">全市场（{{ universe.toLocaleString() }} 只可算）</span>
        </el-tooltip>

        <span class="caliber-chip">{{ maText }}</span>
        <span class="caliber-chip">{{ priceCaliber }}</span>

        <span class="toolbar-fill" />
        <el-popover placement="bottom-end" width="480" trigger="hover" :open-delay="80" popper-class="tk-notice-popper">
          <div class="tk-notice-pop">
            <div class="tk-notice-title">⚠️ 本页是「查找 + 人工复核」工具，不是买入推荐</div>
            <p v-for="(p, i) in noticeParas" :key="i" v-html="p" />
          </div>
          <button slot="reference" type="button" class="notice-trigger" aria-label="页面说明">
            <i class="el-icon-warning-outline" />
            <span>说明</span>
          </button>
        </el-popover>
        <el-button size="small" icon="el-icon-download" :disabled="!rows.length" @click="exportCsv">导出</el-button>
        <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="loadAll">刷新</el-button>
      </div>

      <div class="tb-row">
        <span class="toolbar-label">筛选条件</span>
        <el-tooltip v-for="f in filterDefs" :key="f.key" :content="filterTip(f)" placement="bottom">
          <el-checkbox
            :value="filters[f.key]"
            size="mini"
            :disabled="f.dependsOn && !filters[f.dependsOn]"
            @change="(v) => onToggle(f.key, v)"
          >
            {{ f.label }}<span v-if="coverageOf(f.key) !== null" class="cov">{{ coverageOf(f.key) }}</span><span v-if="f.key === 'c4up'" class="cov" style="color: #c0392b">已回测 · 更差</span>
          </el-checkbox>
        </el-tooltip>
        <span class="toolbar-fill" />
        <span class="hint">开关实时重算漏斗与列表</span>
      </div>

      <div class="tb-row">
        <el-alert
          :title="volAlert"
          type="warning"
          :closable="false"
          show-icon
          class="vol-alert"
        />
      </div>

      <div class="tb-row">
        <span class="toolbar-label">阈值</span>
        <span class="kv-inline">ADX &gt; <b>{{ thresholds.adx }}</b></span>
        <span class="kv-inline">RSI ∈ (<b>{{ thresholds.rsiLow }}</b>, <b>{{ thresholds.rsiHigh }}</b>)</span>
        <span class="kv-inline">量比 &gt; <b>{{ thresholds.volRatio }}</b>（{{ thresholds.volLookback }} 日均量）</span>
        <span class="kv-inline">均线 <b>{{ maWindows.short }}</b>/<b>{{ maWindows.mid }}</b>/<b>{{ maWindows.long }}</b></span>
        <span class="kv-inline">均线斜率回看 <b>{{ thresholds.maSlopeLook }}</b> 日</span>
        <span class="toolbar-fill" />
        <span class="hint">阈值与回测脚本 TH 常量一一对应，本页只读</span>
      </div>
    </div>

    <div v-if="error" class="error-bar">
      <el-alert :title="error" type="warning" :closable="false" show-icon />
    </div>

    <!-- ============ 数据未就绪 ============ -->
    <div v-if="meta && meta.ready === false" class="block">
      <div class="block-head"><h3>数据未就绪</h3></div>
      <div class="block-body">
        <p class="fnote">{{ meta.reason }}</p>
        <p class="fnote">
          本页是<b>只读页面</b>：指标由本地脚本预计算落表（<code>trend_king_signal</code>）后同步到服务器，
          线上不做实时计算 —— 因为本页需要的<b>后复权长历史</b>（<code>market_daily_hfq</code>）在线上不存在，
          且服务器内存不足以全市场实时计算（MA200 至少要 200 个交易日的历史）。
        </p>
      </div>
    </div>

    <template v-else-if="data">
      <!-- ============ 概览卡 ============ -->
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-label">当期通过信号</div>
          <div class="stat-value">{{ counts.current }} <small>只</small></div>
          <div class="stat-sub">
            占可算样本 <b>{{ shareOf(counts.current) }}</b>
            · 较宽松版（不含量比）<b>{{ counts.wide }}</b> 只
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">漏斗最严一环</div>
          <div class="stat-value stat-value-sm">{{ narrowest.label }}</div>
          <div class="stat-sub">
            {{ narrowest.from }} → {{ narrowest.to }}
            （砍掉 <b>{{ narrowest.killPct }}</b>）
            <template v-if="narrowest.isVol">；<b>其中 {{ volDownText }}</b></template>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">市场环境（同日 · 全市场对照）</div>
          <div class="stat-value stat-value-sm">
            <span class="txt-up">{{ market.bull }}</span>
            <span class="txt-muted">多头 vs </span>
            <span class="txt-down">{{ market.bear }}</span>
            <span class="txt-muted">空头</span>
          </div>
          <div class="stat-sub">
            空头是多头的 <b>{{ market.bearRatio === null ? '—' : market.bearRatio.toFixed(2) }}</b> 倍
            · 强空头 ADX {{ market.strongBearAdx }} 只
          </div>
        </div>
        <div class="stat-card alert">
          <div class="stat-label">⚠️ 回测结论（务必先读）</div>
          <div class="stat-value stat-value-alert">本套条件负超额</div>
          <div class="stat-sub">
            全市场 {{ bt.universe.from }}~{{ bt.universe.to }} / {{ bt.configCount }} 个配置<b>逐笔超额全为负</b>，
            达标概率最高仅 <b>{{ pct0(bt.mbbMax) }}</b>（铁律要求 ≥ {{ pct0(bt.mbbRule) }}）。
            定位 = <b>查找 + 人工复核</b>，<b>不是买入推荐</b>
          </div>
        </div>
      </div>

      <!-- ============ 漏斗 + 结构分布 ============ -->
      <div class="two-col">
        <div class="block">
          <div class="block-head">
            <h3>筛选漏斗</h3>
            <span class="hint">{{ universe.toLocaleString() }} 只可算样本 → 逐级收窄</span>
          </div>
          <div class="block-body">
            <div v-for="(s, i) in funnel" :key="s.key" class="funnel-row">
              <div class="funnel-name" v-html="s.label" />
              <div class="funnel-track">
                <div
                  class="funnel-fill"
                  :class="funnelLv(i, s)"
                  :style="{ width: funnelWidth(s) }"
                />
              </div>
              <div class="funnel-val">
                {{ s.n.toLocaleString() }} 只
                <span class="txt-muted">({{ shareOf(s.n) }})</span>
                <em v-if="s.drop !== null" :class="s.drop < 0.3 ? 'mild' : ''">↓{{ pct0(s.drop) }}</em>
              </div>
            </div>
            <p class="fnote">
              「可算样本」= 指标齐全（<code>MA200</code> 需要该股至少 200 个自身交易日）的股票；
              而库内只存「①②③④ 任一命中」的并集（{{ beforeFilter.toLocaleString() }} 只），
              所以第 1 级之后的下降比例会高于全市场口径。
            </p>
          </div>
        </div>

        <div class="block">
          <div class="block-head">
            <h3>结构分布</h3>
            <span class="hint">基于当前结果（{{ rows.length }} 只）</span>
          </div>
          <div class="block-body">
            <div class="sub-title">按行业（Top 10）</div>
            <div v-if="!industryTop.length" class="txt-muted fnote">无结果</div>
            <div v-for="[n, c] in industryTop" :key="n" class="rank-row">
              <div class="rank-name" :title="n">{{ n }}</div>
              <div class="rank-track"><div :style="{ width: rankWidth(c, industryTop) }" /></div>
              <div class="rank-count">{{ c }}</div>
            </div>
            <div class="sub-title mt14">按上市板块</div>
            <div v-if="!boardTop.length" class="txt-muted fnote">无结果</div>
            <div v-for="[n, c] in boardTop" :key="n" class="rank-row">
              <div class="rank-name">{{ n }}</div>
              <div class="rank-track"><div :style="{ width: rankWidth(c, boardTop) }" /></div>
              <div class="rank-count">{{ c }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ 信号表 ============ -->
      <div class="block">
        <div class="block-head">
          <h3>符合条件的股票</h3>
          <span class="hint">
            共 <b>{{ rows.length }}</b> 只 · 按 ADX 降序
            <template v-if="ma20DevMedian !== null">
              · MA20 乖离中位 <b>{{ pct1(ma20DevMedian) }}</b>
            </template>
          </span>
          <span class="toolbar-fill" />
          <span class="hint">点行首箭头展开条件明细</span>
        </div>
        <el-table
          :data="rows"
          size="mini"
          border
          stripe
          :max-height="520"
          row-key="code"
        >
          <el-table-column type="expand">
            <template slot-scope="{ row }">
              <div class="detail">
                <div>
                  <h4>条件命中</h4>
                  <div class="cond"><span class="tick">✓</span>① 均线多头排列 &nbsp;<span class="txt-muted">收盘 &gt; MA20 &gt; MA50 &gt; MA200</span></div>
                  <div class="cond"><span class="tick">✓</span>①′ MA20 近 {{ thresholds.maSlopeLook }} 日斜率 &gt; 0</div>
                  <div class="cond"><span class="tick">✓</span>② ADX(14) = <b>{{ num(row.adx, 1) }}</b> &gt; {{ thresholds.adx }}，且 +DI &gt; −DI</div>
                  <div class="cond"><span class="tick">✓</span>③ RSI(14) = <b>{{ num(row.rsi, 1) }}</b> ∈ ({{ thresholds.rsiLow }}, {{ thresholds.rsiHigh }})</div>
                  <div class="cond"><span class="tick">✓</span>④ 量比 = <b>{{ num(row.volRatio, 2) }}</b> &gt; {{ thresholds.volRatio }}</div>
                  <div class="cond" :class="row.c4up ? '' : 'cond-off'">
                    <span class="tick" :class="row.c4up ? '' : 'tick-off'">{{ row.c4up ? '✓' : '✗' }}</span>
                    ④′ 当日收{{ row.c4up ? '阳' : '阴' }}
                    <span class="txt-muted">（挂 {{ num(row.pctChg, 2) }}%）</span>
                  </div>
                  <div class="cond"><span class="tick">✓</span>⑤ 非 ST（来源：{{ row.stSrc === 'pit' ? 'st_history point-in-time' : '⚠️ 名称兜底' }}）</div>
                </div>
                <div>
                  <h4>关键读数</h4>
                  <div class="kv"><span class="k">MA20 乖离率</span><span class="v">{{ pct2(row.dev20) }} · <span class="pill" :class="devBandCls(row.dev20)">{{ devBandText(row.dev20) }}</span></span></div>
                  <div class="kv"><span class="k">MA50 / MA200 乖离率</span><span class="v">{{ pct2(row.dev50) }} / {{ pct2(row.dev200) }}</span></div>
                  <div class="kv"><span class="k">多头排列已持续</span><span class="v">{{ row.alignDays }} 个交易日</span></div>
                  <div class="kv"><span class="k">+DI / −DI</span><span class="v">{{ num(row.pdi, 1) }} / {{ num(row.ndi, 1) }}</span></div>
                  <div class="kv"><span class="k">流通市值</span><span class="v">{{ mcap(row.mcap) }}</span></div>
                  <div class="kv"><span class="k">不复权 VWAP</span><span class="v">{{ num(row.priceU, 2) }}（仅参考，非 MA 口径）</span></div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="#" type="index" width="46" align="center" />
          <el-table-column label="代码 / 名称" min-width="150">
            <template slot-scope="{ row }">
              <el-link :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer" class="code">{{ row.code }}</el-link>
              <el-link :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer" class="name">{{ row.name || '—' }}</el-link>
              <span class="pill">{{ row.board }}</span>
            </template>
          </el-table-column>
          <el-table-column label="行业" min-width="100" prop="industry" :show-overflow-tooltip="true" />
          <el-table-column label="现价" width="78" align="right">
            <template slot-scope="{ row }">{{ num(row.priceU, 2) }}</template>
          </el-table-column>
          <el-table-column label="涨跌幅" width="84" align="right">
            <template slot-scope="{ row }">
              <span :class="sgn(row.pctChg)">{{ signed(row.pctChg) }}%</span>
            </template>
          </el-table-column>
          <el-table-column label="MA20 乖离" width="96" align="right">
            <template slot-scope="{ row }">
              <span class="pill" :class="devBandCls(row.dev20)" :title="devBandText(row.dev20)">{{ pct1(row.dev20) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="MA50 乖离" width="88" align="right">
            <template slot-scope="{ row }">{{ pct1(row.dev50) }}</template>
          </el-table-column>
          <el-table-column label="多头持续" width="82" align="right">
            <template slot-scope="{ row }">{{ row.alignDays }} 日</template>
          </el-table-column>
          <el-table-column label="ADX" width="70" align="right" sortable :sort-method="(a, b) => a.adx - b.adx">
            <template slot-scope="{ row }"><b>{{ num(row.adx, 1) }}</b></template>
          </el-table-column>
          <el-table-column label="RSI" width="66" align="right">
            <template slot-scope="{ row }">{{ num(row.rsi, 1) }}</template>
          </el-table-column>
          <el-table-column label="量比" width="66" align="right">
            <template slot-scope="{ row }">
              <span :class="row.c4up ? 'txt-strong' : ''">{{ num(row.volRatio, 2) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="收阳" width="62" align="center">
            <template slot-scope="{ row }">
              <el-tag v-if="row.c4up" size="mini" type="danger">阳</el-tag>
              <el-tag v-else size="mini" type="success">阴</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="流通市值" width="92" align="right">
            <template slot-scope="{ row }">{{ mcap(row.mcap) }}</template>
          </el-table-column>
        </el-table>
        <div class="legend">
          <span><span class="pill good">温和</span> MA20 乖离 ≤ 10%</span>
          <span><span class="pill warn">偏高</span> 10% ~ 20%</span>
          <span><span class="pill danger">过度追高</span> &gt; 20%</span>
          <span class="txt-muted">量比加粗 = 当日收阳（④′ 通过）</span>
        </div>
      </div>

      <!-- ============ 口径与风险提示 ============ -->
      <div class="block">
        <div class="block-head"><h3>口径与风险提示</h3></div>
        <div class="block-body notice-stack">
          <div class="notice red">
            <b>① 这套条件经全市场回测为「负超额」，本页不做买入推荐。</b><br>
            用框架原文（均线多头 + ADX&gt;25 + RSI 50~70 + 量比&gt;1.2）在全市场
            {{ bt.universe.codes.toLocaleString() }} 只、{{ bt.universe.from }}~{{ bt.universe.to }} 上测试：
            <b>{{ bt.configCount }} 个配置逐笔超额全部为负</b>，MBB 达标概率最高 <b>{{ pct0(bt.mbbMax) }}</b>
            （项目铁律要求 ≥ {{ pct0(bt.mbbRule) }}）；组合层年化超额
            <b>{{ pct0(bt.portfolioAnnual[0]) }} ~ {{ pct0(bt.portfolioAnnual[1]) }}</b>，
            最大回撤 <b>{{ pct0(bt.portfolioMdd[0]) }} ~ {{ pct0(bt.portfolioMdd[1]) }}</b>。<br>
            消融更直接：<b v-for="(a, i) in ablationUp" :key="a.key">{{ i ? '、' : '' }}{{ a.label }} 改善 {{ pp(a.effect) }}</b>，
            只有 <b>{{ ablationDown.label }} 有正贡献</b>（去掉反而恶化 {{ pp(ablationDown.effect) }}）。
            <b>「越挑越差」</b>。<br>
            <span class="txt-muted">详见 <code>{{ bt.report }}</code> · ④ 口径专项 <code>{{ vu.report }}</code></span>
          </div>

          <div class="notice red">
            <b>② 本页最该看的不是「通过了几条」，而是「追得有多高」。</b><br>
            当前结果的 <b>MA20 乖离率中位数为 {{ pct1(ma20DevMedian) }}</b>
            （区间 {{ pct1(dev20Stats && dev20Stats.min) }} ~ {{ pct1(dev20Stats && dev20Stats.max) }}）——
            也就是<b>信号发出时已经离 20 日均线很远了</b>。回测里「去掉均线条件反而更好」与这条一致：
            这套条件筛出来的不是「更好的机会」，而是「更极端的追涨位置」。
            列表已按 <b>&gt;20% 标红 / 10~20% 标橙 / ≤10% 标绿</b> 分级提示。
          </div>

          <div class="notice red">
            <b>③「放量」这条条件<b>已回测定性</b>：不是「方向不对」，是它本身没有正向信息。</b><br>
            框架原文的「成交量放大确认」隐含 <b>量价齐升</b>。但字面实现只判 <code>量比 &gt; {{ thresholds.volRatio }}</code>，
            <b>没有任何价格方向约束</b> ⇒ 实测当前量比这一级（{{ volDiag ? volDiag.n : 0 }} 只）里
            <b>{{ volDiag ? volDiag.downCount : 0 }} 只（{{ volDiag ? pct0(volDiag.downPct) : '—' }}）当天是下跌的</b>，
            其中 <b>{{ volDiag ? volDiag.deepDownCount : 0 }} 只跌超 5%</b>
            <template v-if="volDiag && volDiag.worst.length">
              （最深 <span v-for="(w, i) in volDiag.worst" :key="w.code">{{ i ? '、' : '' }}{{ w.code }} {{ w.name }} {{ signed(w.pctChg) }}%</span>）
            </template>。<br>
            曾据此推测「补上方向约束就能救回来」。<b>该假设已于 {{ vu.testedAt || '2026-10-01' }} 回测验证 —— 证伪</b>：
            把 ④ 换成 <b>④′（量比 &gt; 阈值 且当日收阳）</b> 后，
            <b>4 个持有期 × 3 个指标 = 12 组比较<b>全部更差</b></b>，h10 逐日超额再恶化
            <b>{{ pp(volUpDelta) }}</b>（逐年看 11 年里只有 <b>{{ (vu.yearsBetter || {}).volUp }}</b> 年更好）。<br>
            ⇒ ④ 的问题是「<b>放量</b>」这个维度本身不携带正向信息；「收阳」本质是<b>追涨</b>，
            与全库最强因子（反转，<code>all_rev60_h10</code> +15.07%）方向相反。<br>
            ④′ 开关<b>保留</b>（供手工对照，勾选后当日只剩
            <b>{{ c4upCount === null ? '—' : c4upCount }}</b> 只），但已标注 <b>「已回测 · 更差」</b> ——
            <b>不要以为打开它更好</b>。<br>
            <span class="txt-muted">详见 <code>{{ vu.report }}</code>。另注意口径：<b>逐笔</b>口径下去掉 ④ 改善
            {{ pp(ablationOf('c4')) }}（09-29 报告），<b>逐日</b>口径下同一档也仍是负超额
            （h10 逐日 −0.5317%、组合年化 −12.95%）—— 两个口径方向一致，但<b>都不足以过线</b>。</span>
          </div>

          <div class="notice gray">
            <b>④ 数据口径</b>（引用数字前必看）：<br>
            · 技术指标用 <b><code>market_daily_hfq</code>（后复权）</b> 计算 —— 均线本就该用复权价，
            且 <code>daily_bar</code> 线上只有 86 个交易日，<b>MA200 在上面根本不存在</b>。<br>
            · 列出的<b>现价用不复权价</b>（<code>amount / volume</code> 的 VWAP），与行情软件盘面一致；
            <b>不要拿它和上面的均线直接相减</b>。<br>
            · <b>流通市值 = <code>amount / turnover</code></b>（亿元）—— 唯一不受后复权污染的口径
            （<code>close × outstanding_share</code> 会放大数倍）。<br>
            · <b>ST 剔除覆盖 {{ stShareText }}</b>（<code>st_history</code> point-in-time 变化点表，回补已完成），
            未覆盖的按「信号日名称含 ST」兜底 ⇒ 仅存极少量前视偏差。<br>
            · 名称 / 行业 / 涨跌幅取自 <code>daily_bar</code> 当日快照。
          </div>
        </div>
      </div>

      <el-collapse class="caliber-collapse">
        <el-collapse-item title="点开看：本页每一步在算什么（与回测口径对照）" name="caliber">
          <table class="mini-table">
            <tbody>
              <tr><td>① 均线多头排列</td><td><code>close &gt; MA{{ maWindows.short }} &gt; MA{{ maWindows.mid }} &gt; MA{{ maWindows.long }}</code>（后复权）</td></tr>
              <tr><td>①′ MA20 上行</td><td><code>MA{{ maWindows.short }} 近 {{ thresholds.maSlopeLook }} 日斜率 &gt; 0</code> —— 价格短暂站上、但 MA20 仍下行时不算</td></tr>
              <tr><td>② ADX 强度</td><td><code>ADX(14) &gt; {{ thresholds.adx }} 且 +DI &gt; −DI</code>（Wilder 平滑）</td></tr>
              <tr><td>③ RSI 区间</td><td><code>RSI(14) ∈ ({{ thresholds.rsiLow }}, {{ thresholds.rsiHigh }})</code> —— 上界 {{ thresholds.rsiHigh }} 是「不追超买」，也是唯一有正贡献的条件</td></tr>
              <tr><td>④ 量比放大</td><td><code>当日量 / 过去 {{ thresholds.volLookback }} 日均量 &gt; {{ thresholds.volRatio }}</code></td></tr>
              <tr><td>④′ 放量须收阳</td><td>在 ④ 基础上追加 <code>close &gt; open</code>（框架原意「量价齐升」）——<b>收益口径尚未回测</b></td></tr>
              <tr><td>⑤ 剔除 ST</td><td>point-in-time：<code>st_history</code> 变化点表（存切换日，不存每日行）</td></tr>
              <tr><td>多头持续天数</td><td>自信号日往前连续满足 ① 的天数 —— 反映趋势「新鲜度」，本页字段</td></tr>
              <tr><td>「较宽松版」</td><td>= 关闭 ④量比 后的通过数。回测显示这一档<b>反而更好</b>，故在概览卡并列展示以便对照</td></tr>
              <tr><td>与回测的关系</td><td>本页只做<b>条件筛选</b>，不产生收益结论。阈值默认值与回测脚本 <code>right-trend-sweep.js</code> 的 <code>TH</code> 常量一一对应</td></tr>
              <tr><td>数据链路</td><td>本地 <code>scripts/build-trend-king-signals.js</code> 预计算 → 落表 <code>trend_king_signal</code> / <code>trend_king_daily</code> → <code>deploy/sync-trend-king.sh</code> 同步 → 本页只读</td></tr>
            </tbody>
          </table>
        </el-collapse-item>
      </el-collapse>
    </template>

    <div v-else class="loading-wrap" v-loading="loading" element-loading-text="正在读取趋势为王数据..." />
  </div>
</template>

<script>
import { request } from '../utils/http.js';
import { stockstarCodeLink, eastmoneyCodeLink } from '../utils/stockLink.js';

export default {
  name: 'TrendKing',
  data() {
    return {
      loading: false,
      error: '',
      meta: null,
      data: null,
      dates: [],
      date: '',
      filters: { c1: true, c1p: true, c2: true, c3: true, c4: true, c4up: false, exSt: true },
      c4upCount: null,
    };
  },
  computed: {
    thresholds() {
      return (this.data && this.data.thresholds) || (this.meta && this.meta.thresholds) || {};
    },
    maWindows() {
      return (this.meta && this.meta.maWindows) || { short: 20, mid: 50, long: 200 };
    },
    priceCaliber() {
      return (this.data && this.data.priceCaliber) || (this.meta && this.meta.priceCaliber) || '';
    },
    maText() {
      const w = this.maWindows;
      return `指标口径：后复权 · MA${w.short}/${w.mid}/${w.long}`;
    },
    bt() {
      return (this.meta && this.meta.backtest) || (this.data && this.data.backtest) || {};
    },
    filterDefs() {
      return (this.meta && this.meta.filters) || [];
    },
    universe() {
      return (this.data && this.data.market && this.data.market.universe) || 0;
    },
    market() {
      return (this.data && this.data.market) || {};
    },
    funnel() {
      return (this.data && this.data.funnel) || [];
    },
    counts() {
      return (this.data && this.data.counts) || { strict: 0, wide: 0, current: 0 };
    },
    beforeFilter() {
      return (this.data && this.data.beforeFilter) || 0;
    },
    rows() {
      return (this.data && this.data.rows) || [];
    },
    volDiag() {
      return this.data && this.data.volDiag;
    },
    dev20Stats() {
      return this.data && this.data.dev20Stats;
    },
    ma20DevMedian() {
      const s = this.dev20Stats;
      return s && Number.isFinite(s.median) ? s.median : null;
    },
    industryTop() {
      return (this.data && this.data.industryTop) || [];
    },
    boardTop() {
      const b = (this.data && this.data.byBoard) || {};
      return Object.entries(b).sort((x, y) => y[1] - x[1]);
    },
    lagDays() {
      return (this.meta && this.meta.dataLag && this.meta.dataLag.lagDays) || 0;
    },
    barLatest() {
      return (this.meta && this.meta.dataLag && this.meta.dataLag.barLatest) || '';
    },
    poolTip() {
      return '回测在**全市场**口径上完成，故本页不提供 top500 / p75 分层池 —— 换池会改变结论而本页不产生收益结论';
    },
    stShareText() {
      const c = this.meta && this.meta.stCoverage;
      if (!c || !c.pool) return '—';
      return `${((c.covered / c.pool) * 100).toFixed(1)}%（${c.covered} / ${c.pool} 只）`;
    },
    volDownText() {
      if (!this.volDiag) return '—';
      return `${this.volDiag.downCount} / ${this.volDiag.n} 只是放量下跌`;
    },
    volAlert() {
      if (!this.volDiag || !this.volDiag.n) return '④「量比 > 1.2」只判量、没有价格方向约束 —— 框架原意的「放量确认」是量价齐升，字面实现抓到的主要是放量下跌';
      return `⚠️ ④ 只判「量比 > ${this.thresholds.volRatio}」，没有方向约束 —— 本日 ${this.volDiag.n} 只里 ${this.volDiag.downCount} 只（${this.pct0(this.volDiag.downPct)}）当天是下跌的，${this.volDiag.deepDownCount} 只跌超 5%。框架原意的「放量确认」是量价齐升，字面实现抓到的主要是放量下跌。④′ 是为此提供的可选补充条件`;
    },
    narrowest() {
      let best = null;
      for (const s of this.funnel) {
        if (s.drop === null) continue;
        if (!best || s.drop > best.drop) best = s;
      }
      const idx = best ? this.funnel.indexOf(best) : -1;
      const from = idx > 0 ? this.funnel[idx - 1].n : 0;
      return {
        label: best ? best.label : '—',
        from: from.toLocaleString(),
        to: best ? best.n.toLocaleString() : '—',
        killPct: best ? this.pct0(best.drop) : '—',
        isVol: !!best && best.key === 'c4',
      };
    },
    ablationUp() {
      return (this.bt.ablation || []).filter((a) => a.effect > 0);
    },
    ablationDown() {
      const list = this.bt.ablation || [];
      return list.find((a) => a.effect < 0) || { label: '—', effect: 0 };
    },
    vu() {
      return (this.bt && this.bt.volUpAblation) || {};
    },
    volUpDelta() {
      const h = (this.vu.byHold || []).find((x) => x.hold === 10);
      return h ? h.delta : null;
    },
    noticeParas() {
      const b = this.bt;
      return [
        '<b>① 不接进【策略回测】</b>：本页筛出的是<b>横截面条件组合</b>，而回测框架测的是'
        + '「按因子排序取 Top-N」；直接套用会把条件组合误当成因子排序。回测结论是<b>负超额</b>'
        + `（${b.configCount} 个配置逐笔全为负），所以本页<b>不做买入推荐</b>。`,
        '<b>② 本页的用途</b>：条件筛选 + <b>人工复核</b>。真正该看的指标是 '
        + '<b>MA20 乖离率</b>（追得有多高），而不是「通过了几条」。',
        '<b>③ ④ 量比条件已回测定性</b>：框架原文的「放量确认」隐含<b>量价齐升</b>，'
        + '但字面实现只判量比、没有价格方向约束，实测抓到的主要是<b>放量下跌</b>。'
        + `第 3 次消融（${b.volUpAblation && b.volUpAblation.testedAt ? b.volUpAblation.testedAt : '2026-10-01'}）显示，`
        + '把 ④ 换成 ④′（量比 + 当日收阳）后 <b>4 个持有期 × 3 个指标 12 组比较全部更差</b> ⇒ <b>假说证伪</b>：'
        + '④ 的问题不是方向，而是<b>「放量」本身不携带正向信息</b>。④′ 开关保留但已标注「已回测 · 更差」。',
        '<b>④ 数据链路</b>：指标在<b>本地</b>用后复权长历史预计算成 <code>trend_king_signal</code> 表后'
        + '同步到服务器，<b>线上只读、不实时算</b>（线上没有 market_daily_hfq，内存也不够）。',
      ];
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    stockstarCodeLink,
    eastmoneyCodeLink,
    /* ---------------- 格式化 ---------------- */
    num(v, d = 2) {
      return v === null || v === undefined || !Number.isFinite(v) ? '—' : v.toFixed(d);
    },
    pct0(v) {
      return v === null || v === undefined || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(0)}%`;
    },
    pct1(v) {
      return v === null || v === undefined || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(1)}%`;
    },
    pct2(v) {
      return v === null || v === undefined || !Number.isFinite(v) ? '—' : `${(v * 100).toFixed(2)}%`;
    },
    pp(v) {
      return v === null || v === undefined || !Number.isFinite(v) ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(4)}pp`;
    },
    signed(v) {
      if (v === null || v === undefined || !Number.isFinite(v)) return '—';
      return `${v >= 0 ? '+' : ''}${v.toFixed(2)}`;
    },
    sgn(v) {
      if (v === null || v === undefined || !Number.isFinite(v)) return 'txt-muted';
      return v >= 0 ? 'txt-up' : 'txt-down';
    },
    mcap(v) {
      if (v === null || v === undefined || !Number.isFinite(v)) return '—';
      return v >= 10000 ? `${(v / 10000).toFixed(2)} 万亿` : `${v.toFixed(0)} 亿`;
    },
    devBandCls(v) {
      if (v === null || v === undefined) return '';
      return v > 0.2 ? 'danger' : v > 0.1 ? 'warn' : 'good';
    },
    devBandText(v) {
      if (v === null || v === undefined) return '—';
      return v > 0.2 ? '过度追高' : v > 0.1 ? '偏高' : '温和';
    },
    shareOf(n) {
      if (!this.universe) return '—';
      return `${((n / this.universe) * 100).toFixed(2)}%`;
    },
    funnelWidth(s) {
      const max = this.funnel.length ? this.funnel[0].n : 0;
      const w = max ? (s.n / max) * 100 : 0;
      return `${Math.max(0.8, w)}%`;
    },
    funnelLv(i, s) {
      if (s.key === '__universe') return 'lv1';
      // 砍掉 ≥ 50% 的一级标红（视觉上指出「最窄的闸门」）
      if (s.drop !== null && s.drop >= 0.5) return 'kill';
      return i <= 2 ? 'lv1' : i <= 4 ? 'lv2' : 'lv3';
    },
    rankWidth(c, list) {
      const max = list.length ? Math.max(...list.map((x) => x[1])) : 1;
      return `${max ? (c / max) * 100 : 0}%`;
    },
    dLabel(d) {
      return this.meta && d === this.meta.latestDate ? `${d}（最新）` : d;
    },
    coverageOf(key) {
      const hit = this.funnel.find((s) => s.key === key);
      if (!hit || !this.universe) return null;
      return this.shareOf(hit.n);
    },
    filterTip(f) {
      const base = {
        c1: `收盘 > MA${this.maWindows.short} > MA${this.maWindows.mid} > MA${this.maWindows.long}（后复权）。全市场单日仅约 8% 通过 —— 这是「右侧趋势」的语义核心`,
        c1p: `MA${this.maWindows.short} 近 ${this.thresholds.maSlopeLook} 日斜率 > 0。筛掉「价格短暂站上但均线仍下行」的假多头`,
        c2: `ADX(14) > ${this.thresholds.adx} 且 +DI > −DI（Wilder 平滑）—— 要求趋势「有强度且有方向」。回测里去掉它超额改善，是纯粹的拖累项`,
        c3: `RSI(14) ∈ (${this.thresholds.rsiLow}, ${this.thresholds.rsiHigh}) —— 上界 ${this.thresholds.rsiHigh} 是「不追超买」。**唯一有正贡献的条件**（去掉它超额恶化）`,
        c4: `当日量 / 过去 ${this.thresholds.volLookback} 日均量 > ${this.thresholds.volRatio}。⚠️ 只看量、不看价 —— 回测显示去掉它超额**大幅改善**，是全场最该质疑的条件`,
        c4up: '在 ④ 之上追加「当日收阳」（close > open）。⚠️ 已回测 · 更差（2026-10-01）：4 个持有期 × 3 个指标 12 组比较全部劣于不加收阳（h10 逐日超额 −1.1206% vs −0.9068%），11 年只有 1 年更好。保留仅供手工对照，不要默认打开',
        exSt: 'point-in-time 剔除 ST / *ST（st_history 变化点表，存变化点不存每日行）。本地回补已 100% 覆盖（5,473 只 / 7,145 个变化点），未覆盖的仍按名称含 ST 兜底',
      }[f.key] || '';
      return base;
    },
    ablationOf(key) {
      const hit = (this.bt.ablation || []).find((a) => a.key === key);
      return hit ? hit.effect : null;
    },
    /* ---------------- 数据 ---------------- */
    async loadAll() {
      this.loading = true;
      this.error = '';
      try {
        const meta = await request('/api/trend-king/meta');
        this.meta = meta;
        if (meta.ready === false) return;
        this.dates = meta.dates || [];
        if (!this.date || !this.dates.includes(this.date)) this.date = meta.latestDate;
        const defs = meta.filters || [];
        const next = { ...this.filters };
        for (const f of defs) next[f.key] = f.default;
        this.filters = next;
        await this.loadSignals();
      } catch (err) {
        this.error = err.message || '加载趋势为王数据失败';
      } finally {
        this.loading = false;
      }
    },
    async loadSignals() {
      if (!this.date) return;
      this.loading = true;
      this.error = '';
      try {
        const qs = new URLSearchParams({ date: this.date });
        for (const [k, v] of Object.entries(this.filters)) qs.set(k, v ? '1' : '0');
        const res = await request(`/api/trend-king/signals?${qs.toString()}`);
        this.data = res;
        await this.loadC4upCount();
      } catch (err) {
        this.error = err.message || '加载信号列表失败';
        this.data = null;
      } finally {
        this.loading = false;
      }
    },
    /** ④′ 的对照数（勾选 ④′ 后剩多少）—— 与当前开关无关，单独取一次 */
    async loadC4upCount() {
      const need = this.filters.c1 && this.filters.c1p && this.filters.c2 && this.filters.c3 && this.filters.exSt;
      if (!need) {
        this.c4upCount = null;
        return;
      }
      try {
        const qs = new URLSearchParams({ date: this.date, c1: '1', c1p: '1', c2: '1', c3: '1', c4: '1', c4up: '1', exSt: '1' });
        const res = await request(`/api/trend-king/signals?${qs.toString()}`);
        this.c4upCount = res.counts.current;
      } catch (_) {
        this.c4upCount = null;
      }
    },
    onToggle(key, val) {
      this.filters = { ...this.filters, [key]: val };
      if (key === 'c4' && !val) this.filters.c4up = false;
      this.loadSignals();
    },
    /* ---------------- 导出 ---------------- */
    exportCsv() {
      const head = [
        '代码', '名称', '板块', '行业', '现价(不复权VWAP)', '涨跌幅%',
        'MA20乖离%', 'MA50乖离%', 'MA200乖离%', '多头持续(日)',
        'ADX', '+DI', '-DI', 'RSI', '量比', '当日收阳', '流通市值(亿)', '信号日', 'ST来源',
      ];
      const lines = this.rows.map((r) => [
        r.code, r.name, r.board, r.industry, r.priceU, r.pctChg,
        r.dev20 === null ? '' : (r.dev20 * 100).toFixed(2),
        r.dev50 === null ? '' : (r.dev50 * 100).toFixed(2),
        r.dev200 === null ? '' : (r.dev200 * 100).toFixed(2),
        r.alignDays, r.adx, r.pdi, r.ndi, r.rsi, r.volRatio,
        r.c4up ? '阳' : '阴', r.mcap, this.date, r.stSrc,
      ]);
      const csv = [head, ...lines]
        .map((row) => row.map((v) => (v === null || v === undefined ? '' : String(v))).join(','))
        .join('\n');
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trend-king-${this.date}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.tk-page {
  padding: 4px;
}

.toolbar {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 14px;
}

.tb-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
}

.tb-row + .tb-row {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #ebeef5;
}

.toolbar-label {
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
}

.toolbar-fill {
  flex: 1 1 auto;
}

.sel-date {
  width: 168px;
}

.cov {
  margin-left: 5px;
  font-size: 11px;
  color: #909399;
}

.caliber-chip {
  font-size: 12px;
  color: #909399;
  border: 1px dashed #dcdfe6;
  border-radius: 10px;
  padding: 1px 8px;
}

/* 与神奇九转「说明」一致：浅红胶囊，避免看起来像普通灰按钮 */
.notice-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #f5c6cb;
  border-radius: 14px;
  background: #fef0f0;
  color: #f56c6c;
  font-size: 12px;
  cursor: help;
  outline: none;
  font-family: inherit;
}

.notice-trigger:hover {
  background: #fde2e2;
  border-color: #f56c6c;
}

.notice-trigger i {
  font-size: 14px;
}

.kv-inline {
  font-size: 12.5px;
  color: #606266;
  white-space: nowrap;
}

.kv-inline b {
  color: #303133;
}

.vol-alert {
  flex: 1 1 auto;
  padding: 4px 8px;
}

.vol-alert >>> .el-alert__title {
  font-size: 12.5px;
  line-height: 1.6;
}

.error-bar {
  margin-bottom: 14px;
}

/* ---------- 概览卡 ---------- */
.stat-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.stat-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 14px;
  min-width: 0;
}

.stat-card.alert {
  border-color: #fbc4c4;
  background: #fef9f9;
}

.stat-card.alert .stat-label {
  color: #c45656;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

.stat-value small {
  font-size: 12px;
  font-weight: 400;
  color: #606266;
}

.stat-value-sm {
  font-size: 14px;
  font-weight: 600;
}

.stat-value-alert {
  font-size: 16px;
  font-weight: 700;
  color: #c45656;
}

.stat-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  line-height: 1.6;
}

/* ---------- 两列 ---------- */
.two-col {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 14px;
  align-items: start;
}

.block {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  margin-bottom: 14px;
}

.two-col .block {
  margin-bottom: 0;
}

.block-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #ebeef5;
}

.block-head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.hint {
  font-size: 12px;
  color: #909399;
}

.block-body {
  padding: 12px 14px;
}

.sub-title {
  font-size: 12.5px;
  color: #909399;
  margin-bottom: 6px;
}

.mt14 {
  margin-top: 14px;
}

/* ---------- 漏斗 ---------- */
.funnel-row {
  display: grid;
  grid-template-columns: 138px 1fr 168px;
  gap: 10px;
  align-items: center;
  margin: 7px 0;
  font-size: 13px;
}

.funnel-name {
  color: #606266;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.funnel-track {
  background: #f5f7fa;
  border-radius: 3px;
  height: 20px;
  overflow: hidden;
}

.funnel-fill {
  height: 100%;
  border-radius: 3px;
  background: #409EFF;
}

.funnel-fill.lv1 { background: #a0cfff; }
.funnel-fill.lv2 { background: #79bbff; }
.funnel-fill.lv3 { background: #409EFF; }
.funnel-fill.kill { background: #F56C6C; }

.funnel-val {
  font-variant-numeric: tabular-nums;
  color: #303133;
  white-space: nowrap;
}

.funnel-val em {
  font-style: normal;
  color: #F56C6C;
  font-size: 12px;
  margin-left: 5px;
}

.funnel-val em.mild {
  color: #E6A23C;
}

/* ---------- 分布 ---------- */
.rank-row {
  display: grid;
  grid-template-columns: 90px 1fr 34px;
  gap: 10px;
  align-items: center;
  margin: 6px 0;
  font-size: 13px;
}

.rank-name {
  color: #606266;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rank-track {
  background: #f5f7fa;
  border-radius: 3px;
  height: 16px;
  overflow: hidden;
}

.rank-track > div {
  height: 100%;
  background: #b3d8ff;
  border-radius: 3px;
}

.rank-count {
  color: #909399;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

/* ---------- 表格 ---------- */
.legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  padding: 8px 14px 10px;
  font-size: 12px;
  color: #606266;
}

.pill {
  display: inline-block;
  font-size: 11px;
  padding: 1px 7px;
  border-radius: 9px;
  background: #f0f2f5;
  color: #606266;
  margin-left: 5px;
  white-space: nowrap;
}

.pill.warn { background: #fdf6ec; color: #b88230; }
.pill.danger { background: #fef0f0; color: #c45656; }
.pill.good { background: #f0f9eb; color: #529b2e; }

.code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: #409EFF;
  margin-right: 6px;
}

.name {
  font-weight: 500;
  color: #409EFF;
}

/* ---------- 展开明细 ---------- */
.detail {
  background: #f8fbfe;
  border: 1px solid #d9ecff;
  border-radius: 4px;
  padding: 12px 14px;
  margin: 4px 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 22px;
  font-size: 12.5px;
  text-align: left;
}

.detail h4 {
  margin: 0 0 8px;
  font-size: 12.5px;
  color: #409EFF;
  font-weight: 600;
}

.kv {
  display: flex;
  justify-content: space-between;
  padding: 3px 0;
  border-bottom: 1px dashed #e8f0fa;
}

.kv:last-child {
  border-bottom: 0;
}

.kv .k {
  color: #909399;
}

.kv .v {
  color: #303133;
  font-variant-numeric: tabular-nums;
}

.cond {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
}

.cond .tick {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #67C23A;
  color: #fff;
  font-size: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 14px;
}

.cond-off {
  color: #c0c4cc;
}

.tick-off {
  background: #c0c4cc;
}

/* ---------- 提示 ---------- */
.notice-stack {
  display: grid;
  gap: 12px;
}

.notice {
  border-radius: 4px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.8;
}

.notice.red {
  background: #fef0f0;
  border: 1px solid #fbc4c4;
  color: #a8433c;
}

.notice.gray {
  background: #fafafa;
  border: 1px solid #ebeef5;
  color: #606266;
}

.notice b {
  color: #303133;
}

.fnote {
  font-size: 12.5px;
  color: #606266;
  line-height: 1.8;
  margin: 8px 0 0;
}

.caliber-collapse {
  margin-bottom: 14px;
}

.caliber-collapse >>> .el-collapse-item__header {
  font-size: 13px;
  color: #409EFF;
  background: #fafafa;
  padding: 0 14px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  height: 42px;
  line-height: 42px;
}

.caliber-collapse >>> .el-collapse-item__wrap {
  border: 1px solid #ebeef5;
  border-top: 0;
  border-radius: 0 0 6px 6px;
  background: #fff;
}

.caliber-collapse >>> .el-collapse-item__content {
  padding: 10px 14px;
}

.mini-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.mini-table td {
  padding: 6px 8px;
  border: 1px solid #ebeef5;
  vertical-align: top;
  color: #606266;
}

.mini-table td:first-child {
  width: 150px;
  color: #909399;
  background: #fafafa;
}

.txt-up { color: #F56C6C; }
.txt-down { color: #67C23A; }
.txt-muted { color: #909399; }
.txt-strong { font-weight: 700; color: #303133; }

.loading-wrap {
  min-height: 320px;
}

.tk-page >>> .el-table .cell {
  word-break: break-word;
}

code {
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 3px;
  padding: 0 4px;
  font-size: 12px;
}

@media (max-width: 1100px) {
  .two-col {
    grid-template-columns: 1fr;
  }
  .funnel-row {
    grid-template-columns: 110px 1fr 150px;
  }
  .detail {
    grid-template-columns: 1fr;
  }
}
</style>

<!-- popover 挂到 body，需非 scoped -->
<style>
.tk-notice-popper {
  padding: 12px 14px !important;
}

.tk-notice-pop .tk-notice-title {
  font-size: 13px;
  font-weight: 600;
  color: #f56c6c;
  margin-bottom: 8px;
}

.tk-notice-pop p {
  margin: 0 0 8px;
  font-size: 12.5px;
  line-height: 1.7;
  color: #606266;
}

.tk-notice-pop p:last-child {
  margin-bottom: 0;
}
</style>
